import type {
  AiDetectorAnalysisData,
  ContentOverviewData,
  SeoOverviewData,
  SeoPriorityTone,
  SeoStructureItem
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import mapAiDetectionData from './mapAiDetectionData';
import mapAiSearchData from './mapAiSearchData';
import mapEeatData from './mapEeatData';
import { getScoreGaugeVerdict } from '@/app/ContentTools/components/ai-content-analysis/AiLikenessGauge';

const getSeoVerdict = (score: number, max = 10) =>
  getScoreGaugeVerdict((score / max) * 100);

const getHealthTone = (priority: string): SeoPriorityTone => {
  const value = priority.trim();
  if (value.includes('بحرانی')) return 'danger';
  if (value.includes('سریع')) return 'warning';
  if (value.includes('فرصت') || value.includes('متوسط')) return 'info';
  return 'success';
};

const scoresToItems = (
  scores: Record<string, number> | undefined
): SeoStructureItem[] =>
  Object.entries(scores || {}).map(([name, score]) => ({ name, score }));

const emptyDetector = (id: string, keyword: string): AiDetectorAnalysisData => ({
  id,
  keyword,
  ai_likeness: 0,
  human_likeness: 0,
  confidence: '',
  verdict: '',
  criteria: [],
  ai_signals: [],
  human_signals: [],
  message: ''
});

export const mapContentOverviewSeo = (
  data: Pick<ContentOverviewData, 'keyword' | 'url' | 'seo_overview'>
): SeoOverviewData => {
  const seo = data.seo_overview;
  const structure = scoresToItems(seo?.seo_scores);

  return {
    keyword: data.keyword || '',
    url: data.url || '',
    overall_score: seo?.seo_score ?? 0,
    overall_max: 10,
    verdict: getSeoVerdict(seo?.seo_score ?? 0),
    strengths_count: seo?.strengths_count ?? 0,
    weaknesses_count: seo?.weaknesses_count ?? 0,
    suggestions_count: seo?.improvements_count ?? 0,
    structure,
    criterion_scores: structure,
    health_items: (seo?.seo_health_breakdown || []).map(item => ({
      category: item.category,
      finding: item.findings,
      priority: item.priority,
      tone: getHealthTone(item.priority)
    }))
  };
};

export const mapContentOverviewDetector = (
  data: ContentOverviewData
): AiDetectorAnalysisData =>
  mapAiDetectionData({
    id: data.id,
    keyword: data.keyword,
    ai_detection_report: data.ai_detection_report || undefined,
    ...data.ai_detection_report
  }) || emptyDetector(data.id, data.keyword);

export const mapContentOverview = (data: ContentOverviewData) => {
  const eeat =
    data.eeat_report || data.eeat_status
      ? mapEeatData({
          eeat_report: data.eeat_report,
          eeat_status: data.eeat_status ?? undefined
        })
      : null;

  const geo =
    data.ai_search_report || data.ai_search_status
      ? mapAiSearchData({
          ai_search_report: data.ai_search_report,
          ai_search_status: data.ai_search_status ?? undefined
        })
      : null;

  return {
    seo: data.seo_overview ? mapContentOverviewSeo(data) : null,
    detector: mapContentOverviewDetector(data),
    eeat,
    geo,
    showDetection:
      data.detection_status != null || data.ai_detection_report != null,
    detectionStatus: data.detection_status ?? null,
    eeatStatus: data.eeat_status ?? null,
    geoStatus: data.ai_search_status ?? null,
    isDetectionPending:
      data.detection_status === 'inserted' ||
      data.detection_status === 'processing',
    isEeatPending:
      data.eeat_status === 'inserted' || data.eeat_status === 'processing',
    isGeoPending:
      data.ai_search_status === 'inserted' ||
      data.ai_search_status === 'processing'
  };
};
